<template>
    <div
        :class="[
            valueIcon || props.icon ? 'with-icon' : '',
            props.isError ? 'with-error' : '',
            data.isOpenDropdown ? 'opened' : '',
            props.disabled ? 'disabled' : '',
            'select-field'
        ]"
    >
        <div @click="toggleOpened">
            <input
                ref="field"
                autocomplete="off"
                :disabled="props.disabled"
                :placeholder="props.placeholder"
                :value="valueTitle"
                :class="{
                    'without-placeholder': !props.placeholder,
                    clearable: props.clearable && data.selected && !props.disabled
                }"
            />

            <span class="placeholder" :title="props.placeholder">{{ props.placeholder }}</span>

            <Icon v-if="valueIcon || props.icon"
                :icon="valueIcon || props.icon"
                :sprite="valueSprite || props.sprite"
            />


            <ModalBox v-if="data.isOpenDropdown"
                :field="field"
                :placeOptions="{
                    leftAuto: props.leftAuto,
                    rightAuto: props.rightAuto
                }"
                @close="toggleOpened"
                @onSwipeBottom="toggleOpened"
            >
                <ul>
                    <li
                        v-for="option in props.optionList"
                        :value="option.code"
                        :key="option.code"
                        @click.stop="onChoose($event, option)"
                        :class="{ selected: data.selected === option.code }"
                    >

                        <slot :option="option">
                            <Icon v-if="option.icon" :icon="option.icon" :sprite="option.sprite" />
                            <span>{{ props.useI18n ? t(option.name) : option.name }}</span>
                        </slot>

                        <Icon v-show="data.selected === option.code" icon="check" sprite="actions" class="check" />
                    </li>
                </ul>
            </ModalBox>

            <div class="actions-block">
                <IconButton
                    v-if="props.clearable && data.selected && !props.disabled"
                    tabindex="-1"
                    icon="close" sprite="navigation"
                    class="tertiary size-s"
                    @click.stop="clean"
                />

                <Icon icon="chevron-down" sprite="navigation" class="arrow-down" />
            </div>
        </div>
        <span class="error">{{ props.error }}</span>
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import IconButton from '🔗/components/ui/IconButton.vue';
import ModalBox from '🔗/components/ui/ModalBox.vue';

const { t } = useI18n();
const field = ref(null);

const props = defineProps({
    isError: { type: Boolean, default: false },
    icon: { type: String, default: '' },
    sprite: { type: String, default: '' },
    name: { type: String, default: '' },
    value: { type: [Number, String, Boolean], default: null },
    position: { type: Number, default: 0 },
    optionList: {
        type: Array,
        default: () => []
    },
    leftAuto: { type: Boolean, default: true },
    rightAuto: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    error: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    useI18n: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true }
});

const data = reactive({
    selected: null,
    isOpenDropdown: false
});

const valueTitle = computed(() => {
    const name = data.selected === null ? '' : props.optionList.find((option) => option.code === data.selected)?.name;

    return name && props.useI18n ? t(name) : name;
});

const valueIcon = computed(() =>
    data.selected === null ? '' : props.optionList.find((option) => option.code === data.selected)?.icon
);

const valueSprite = computed(() =>
    data.selected === null ? '' : props.optionList.find((option) => option.code === data.selected)?.sprite
);

const emit = defineEmits(['change']);

const clean = () => {
    field.value.value = '';
    data.selected = null;
    emit('change', {
        name: props.name,
        value: null,
        position: props.position
    });
};

const toggleOpened = () => {
    if (props.disabled) {
        return;
    }

    data.isOpenDropdown = !data.isOpenDropdown;

    if (!data.isOpenDropdown) {
        return false;
    }
};

onMounted(() => {
    data.selected = props.value;
});

watch(() => props.value, (value) => {
    if (value === null && data.selected) {
        data.selected = null;
        return;
    }

    data.selected = value;
});

const hide = () => (data.isOpenDropdown = false);

const onChoose = (event, payload) => {
    data.selected = payload.code;
    data.isOpenDropdown = false;

    emit('change', {
        name: props.name,
        value: payload.code,
        position: props.position
    });
};
</script>

<style lang="scss">
.select-field {
    display: flex;
    flex-direction: column;
    & > div {
        margin: 0 0 8px;
        display: flex;
        position: relative;
        cursor: pointer;
        width: 100%;

        &:first-child {
            max-width: var(--input-max-width);
        }
        // max-width: var(--input-max-width);

        .placeholder {
            @include input-placeholder;
            // padding-right: 80px;
        }

        .actions-block {
            position: absolute;
            top: 0;
            height: 100%;
            right: 8px;
            display: flex;
            align-items: center;

            & > svg {
                fill: var(--dark-80);
                stroke: var(--dark-80);
                height: 20px;
                width: 20px;
                margin: 0 0 0 8px;

                &.arrow-down {
                    fill: var(--dark-80);
                    transition: all 0.4s;
                }

            }
        }

        & > svg {
            &:not(.arrow-down) {
                position: absolute;
                left: 16px;
                top: 50%;
                transform: translateY(-50%);
                width: 20px;
                height: 20px;
                fill: var(--dark-80);
                stroke: var(--dark-80);
            }
        }

        input {
            @include input;
            padding-right: 44px;
            pointer-events: none;
            &.clearable {
                padding-right: 80px;
            }
        }

        // input[disabled] {
        //     &:not(:placeholder-shown) {
        //         color: var(--dark-500);
        //         background: var(--white-500);
        //         border: 1px solid var(--gray-20);
        //         // color: var(--dark-20);
        //         // background: var(--light-10);
        //         // border: 1px solid var(--light-20);
        //     }
        // }

        &:hover {
            input:not([disabled]) {
                border-color: var(--primary-500);
            }
        }
    }

    &.disabled {
        & > div {
            cursor: default;
        }
    }

    &.opened {
        & > div {
            .arrow-down {
                transform: rotate(180deg);
            }

            input {
                border-color: var(--primary-500);
            }
        }
    }

    & > .error {
        font: var(--font-label);
        color: var(--secondary-500) !important;
        display: flex;
        min-height: 11px;
    }

    &.with-error {
        & > div {
            input {
                border-color: var(--secondary-500) !important;
            }

            svg,
            input:not(:placeholder-shown) + .placeholder + svg,
            input:focus + .placeholder + svg {
                fill: var(--secondary-500) !important;
                stroke: var(--secondary-80) !important;
            }

            input:not(:placeholder-shown) + .placeholder + svg,
            svg {
                color: var(--secondary-500) !important;
            }
        }
    }

    &.with-icon {
        & > div {
            input {
                padding-left: 52px;
            }

            .placeholder {
                left: 53px;
            }
        }
    }

    &.light {
        & > div {
            input {
                padding-top: 8px;
                padding-right: 16px;
                padding-bottom: 8px;
                height: 38px;
            }

            svg {
                height: 18px;
                width: 18px;
            }

            input:not(:placeholder-shown) + .placeholder,
            input:focus + .placeholder {
                display: none;
            }
        }
    }

    &.with-placeholder {
        & > div {
            input:not(:placeholder-shown) + .placeholder,
            input:focus + .placeholder {
                display: flex;

                top: -6px;
                background: var(--white-500);
            }
        }
    }

    &.noerror {
        & > div {
            margin: 0;
        }
        & > .error {
            display: none;
        }
    }


    &.inline {
        & > div {
            input {
                border-color: transparent !important;
                background: transparent;
                backdrop-filter: blur(8px);
                -webkit-backdrop-filter: blur(8px);
                border-radius: 0;
            }

            .actions-block {
                right: 16px;
            }
            & > svg:not(.arrow-down) {
                fill: var(--dark-80) !important;
                stroke: var(--dark-80) !important;
            }
        }
    }

    &.icon-only {
        & > div {
            width: 44px;
            height: 44px;
            .placeholder,
            input {
                width: 44px;
                opacity: 0;
                padding: 0;
            }

            & > svg {
                left: 50%;
                top: 50%;
                transform: translateX(-50%) translateY(-50%);
            }

            .actions-block {
                display: none;
            }
        }
    }
}

.direction-rtl .select-field {
    & > div {
        .placeholder {
            padding-right: 0;
            padding-left: 80px;
            left: auto;
            right: 20px;
        }

        .actions-block {
            right: auto;
            left: 8px;
        }

        & > svg {
            &:not(.arrow-down) {
                left: auto;
                right: 16px;
            }
        }

        input {
            padding-right: 20px;
            padding-left: 44px;
            &.clearable {
                padding-left: 80px;
                padding-right: 20px;
            }
        }
    }

    &.with-icon {
        & > div {
            input {
                padding-right: 52px;
                &:not(.clearable) {
                    padding-left: 20px;
                }
            }

            .placeholder {
                left: auto;
                right: 53px;
            }
        }
    }

    &.inline {
        & > div {
            .actions-block {
                right: auto;
                left: 16px;
            }
        }
    }

    &.icon-only {
        & > div {
            & > svg {
                left: auto;
                right: 50%;
                transform: translateX(50%) translateY(-50%);
            }
        }
    }
}
</style>
